import { trackIdentifiedEvent } from '@/components/bily-tracking';
import { useBilyCustomer } from '@/hooks/useBilyIdentify';
import { ApiClient } from '@/services/api-client';
import { SubscriptionStatus } from '@/services/types/Billing.types';
import { useAuth } from '@clerk/nextjs';
import { AxiosResponse } from 'axios';
import { usePostHog } from 'posthog-js/react';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const SUBSCRIPTION_POLL_INTERVAL_MS = 30_000;

type SubscriptionContextProps = {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  hasActiveSubscription: boolean;
  refetchSubscription: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextProps>({
  subscription: null,
  loading: true,
  hasActiveSubscription: false,
  refetchSubscription: async () => {},
});

type SubscriptionProviderProps = {
  children: ReactNode;
};

const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const { isLoaded, orgId } = useAuth();
  const queryClient = useQueryClient();

  // ── Subscription status (polled every 30s) ───────────────────────────
  // HasActiveSubscription from the API already includes BypassSubscriptionCheck,
  // so no separate org query is needed for access decisions.
  const qSubscription = useQuery({
    queryKey: ['billing.getSubscriptionStatus', orgId],
    queryFn: async () => {
      const res = (await ApiClient.billing.getSubscriptionStatus()) as unknown as AxiosResponse<{
        HasActiveSubscription: boolean;
        Subscription: SubscriptionStatus | null;
        OrgExists?: boolean;
      }>;
      return res.data;
    },
    enabled: isLoaded,
    refetchInterval: SUBSCRIPTION_POLL_INTERVAL_MS,
    staleTime: 10_000,
  });

  // ── Redirect to org creation if backend reports the org no longer exists ──
  // This happens when a cleanup migration deletes DDB records but Clerk still
  // has the org, so the user lands on the dashboard with a stale JWT.
  useEffect(() => {
    if (qSubscription.data && qSubscription.data.OrgExists === false) {
      // eslint-disable-next-line no-console
      console.error(
        '[SubscriptionProvider] Org does not exist in backend — redirecting to org creation'
      );
      window.location.href = '/organizations/create';
    }
  }, [qSubscription.data]);

  const subscription = qSubscription.data?.Subscription ?? null;
  const loading = qSubscription.isLoading;
  const hasActiveSubscription = qSubscription.data?.HasActiveSubscription ?? false;

  // ── Enrich PostHog person properties with subscription status ────────
  const posthog = usePostHog();
  const lastStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = subscription?.Status ?? 'none';
    if (posthog && status !== lastStatusRef.current) {
      lastStatusRef.current = status;
      posthog.setPersonProperties({
        subscription_status: status,
        has_active_subscription: hasActiveSubscription,
      });
    }
  }, [posthog, subscription?.Status, hasActiveSubscription]);

  // ── Refresh Bily customer context on a verified subscription change ──
  // Seeded from the first observed status so a page load never looks like a
  // transition; only a real in-session change emits an event.
  const bilyCustomer = useBilyCustomer();
  const bilyStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (qSubscription.isLoading || !qSubscription.data) return;
    const status = subscription?.Status ?? 'none';

    if (bilyStatusRef.current === null) {
      bilyStatusRef.current = status;
      return;
    }
    if (bilyStatusRef.current === status) return;

    const previousStatus = bilyStatusRef.current;
    bilyStatusRef.current = status;
    if (!bilyCustomer) return;

    // First activation (no prior subscription) is the same business moment as
    // the confirmed payment, which already emits `Trial Started` from
    // src/pages/payment/success.tsx. Reporting it again here would double-count
    // the conversion — and it is not a renewal.
    if (previousStatus === 'none') return;

    const event =
      status === 'CANCELED' || status === 'EXPIRED'
        ? 'Subscription Cancelled'
        : status === 'ACTIVE' && (previousStatus === 'PAYMENT_DUE' || previousStatus === 'UNPAID')
          ? 'Subscription Renewed'
          : 'Subscription Updated';

    trackIdentifiedEvent(
      event,
      bilyCustomer.user,
      {
        ...bilyCustomer.account,
        plan: subscription?.ProductId,
        subscriptionStatus: status,
      },
      {
        bily_business_event: event,
        bily_funnel_stage: 'retention',
        previous_subscription_status: previousStatus,
        cancel_at_period_end: subscription?.CancelAtPeriodEnd,
      }
    );
  }, [
    qSubscription.isLoading,
    qSubscription.data,
    subscription?.Status,
    subscription?.ProductId,
    subscription?.CancelAtPeriodEnd,
    bilyCustomer,
  ]);

  const refetchSubscription = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing.getSubscriptionStatus'] });
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        hasActiveSubscription,
        refetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

const useSubscription = () => useContext(SubscriptionContext);

export { SubscriptionProvider, useSubscription };
